// Local replacement for the abandoned `react-break` package (frozen at React 16,
// no React 18 release). Reproduces the single usage pattern in this app:
//
//   <Break query={{ method: 'is', breakpoint: 'hasCarousel' }} breakpoints={{ hasCarousel: 0, hasImages: 1025 }}>
//     ...children...
//   </Break>
//
// `breakpoints` maps a name -> minimum viewport width (px). The "active"
// breakpoint is the one with the largest threshold that is <= the current width.
// method 'is' renders children only when the active breakpoint === query.breakpoint.
// (Only the 'is' method is used in this codebase.)

import { useState, useEffect } from 'react'

function getWidth() {
  return typeof window !== 'undefined' ? window.innerWidth : 0
}

function getActiveBreakpoint(breakpoints, width) {
  // Sort names by threshold ascending, pick the last one whose threshold <= width.
  const entries = Object.entries(breakpoints).sort((a, b) => a[1] - b[1])
  let active = entries.length ? entries[0][0] : undefined
  for (const [name, threshold] of entries) {
    if (width >= threshold) {
      active = name
    }
  }
  return active
}

const Break = ({ query, breakpoints, children }) => {
  const [width, setWidth] = useState(getWidth())

  useEffect(() => {
    const onResize = () => setWidth(getWidth())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  if (!query || !breakpoints) return null

  const active = getActiveBreakpoint(breakpoints, width)
  const method = query.method || 'is'

  let shouldRender = false
  if (method === 'is') {
    shouldRender = active === query.breakpoint
  } else if (method === 'min') {
    shouldRender = width >= (breakpoints[query.breakpoint] ?? Infinity)
  } else if (method === 'max') {
    shouldRender = width < (breakpoints[query.breakpoint] ?? -Infinity)
  }

  return shouldRender ? children : null
}

export default Break
